import type { TransferView } from '../files/useFileTransfer';
import { formatBytes } from '../files/protocol';

/** 보이는 알림 — 버튼(`action`)은 세션 창에서만 있다(파일 창으로는 함수를 보낼 수 없다). */
export interface TransferNotice {
  kind: 'ok' | 'err';
  text: string;
  action?: { label: string; run: () => void };
}

/**
 * 파일 전송 진행률(전송량·[취소]) + 결과 알림(설계서 §9.4). 그리기만 한다 — 로직은 `useFileTransfer` 에 있다.
 * 세션 도구 막대와 파일 창(`FilePanel` place=window)이 **같은 부품**을 쓴다. 알림 모양은 클립보드 알림과 같은 스타일이다.
 *
 * 종전의 도구 막대 [📁 파일 보내기]는 파일 화면([🗂 파일])의 [파일 보내기…]·[폴더 보내기…]로 옮겼다(2026-09-29 사용자 결정 —
 * 파일 메뉴가 따로 있어 중복).
 *
 * @param refocus 버튼을 누른 뒤 포커스를 돌려줄 곳(세션 창 = 키보드 캡처 칸). 없으면 아무것도 하지 않는다.
 */
export function TransferStatus({
  transfer: t,
  notice,
  onCancel,
  onDismiss,
  refocus = () => undefined,
}: {
  transfer: TransferView | null;
  notice: TransferNotice | null;
  onCancel: () => void;
  onDismiss: () => void;
  refocus?: () => void;
}) {
  const pct = t && t.total > 0 ? Math.floor((t.done / t.total) * 100) : 0;
  return (
    <>
      {t && (
        <span className="xfer" role="status" title={t.summary}>
          <span className="xfer-dir">{t.direction === 'up' ? '⬆ 원격으로' : '⬇ 내 PC로'}</span>
          <span className="xfer-bar" aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </span>
          <span className="xfer-num">
            {pct}% · {formatBytes(t.done)} / {formatBytes(t.total)}
          </span>
          <button
            className="ghost"
            onClick={() => {
              onCancel();
              refocus();
            }}
          >
            취소
          </button>
        </span>
      )}
      {notice && (
        <span className={`clip-toast ${notice.kind}`} role="status" title={notice.text}>
          <span className="clip-toast-text">{notice.text}</span>
          {notice.action && (
            <button
              className="ghost"
              onClick={() => {
                notice.action?.run();
                onDismiss();
                refocus();
              }}
            >
              {notice.action.label}
            </button>
          )}
          <button
            className="clip-toast-close"
            aria-label="닫기"
            onClick={() => {
              onDismiss();
              refocus();
            }}
          >
            ×
          </button>
        </span>
      )}
    </>
  );
}
